import { Platform, useWindowDimensions } from "react-native";

/**
 * The width at or above which the desktop shell (ADR-0020) takes over: persistent left navigation,
 * comfortable data tables, and the ⌘K palette. A fixed breakpoint, not fluid: below it the app
 * renders its compact web chrome unchanged, so mobile web and desktop never share one half-adapted
 * layout. 1024px is the laptop and desktop floor; the platform guard keeps native devices on the
 * app surface, where the separate rail breakpoints below apply.
 */
export const DESKTOP_BREAKPOINT = 1024;
export const IOS_RAIL_BREAKPOINT = 744;
export const ANDROID_RAIL_BREAKPOINT = 600;

export const isDesktopPlatform = (platform: string, width: number): boolean =>
  platform === "web" && width >= DESKTOP_BREAKPOINT;

export const usesNavigationRail = (platform: string, width: number): boolean =>
  platform === "ios"
    ? width >= IOS_RAIL_BREAKPOINT
    : platform === "android" && width >= ANDROID_RAIL_BREAKPOINT;

export const usesExpandedLayout = (platform: string, width: number): boolean =>
  isDesktopPlatform(platform, width) || usesNavigationRail(platform, width);

/**
 * True when the viewport is wide enough for the desktop shell. Reads `useWindowDimensions`, so it
 * re-renders on resize (a browser window dragged across the breakpoint flips chrome live) and on
 * orientation change. The desktop shell remains web-only; native tablets use the separate rail
 * and expanded-layout breakpoints above.
 */
export function useIsDesktop(): boolean {
  const { width } = useWindowDimensions();
  return isDesktopPlatform(Platform.OS, width);
}

export function useNavigationRail(): boolean {
  const { width } = useWindowDimensions();
  return usesNavigationRail(Platform.OS, width);
}

export function useExpandedLayout(): boolean {
  const { width } = useWindowDimensions();
  return usesExpandedLayout(Platform.OS, width);
}
